import HintTooltip from "components/Ui/HintTooltip/HintTooltip";
import notfound from "assets/images/notfound.png";
import DropDownButtons from "components/Ui/DropDownButtons/DropDownButtons";
import TableBodyCells from "components/Ui/TableBodyCells/TableBodyCells";
import BlockStatusButton from "components/Ui/BlockStatusButton/BlockStatusButton";
import PercentageCell from "components/Ui/PercentageCell/PercentageCell";
import OrderLimitCell from "components/Ui/OrderLimitCell/OrderLimitCell";
import WalletModeCell from "components/Ui/WalletModeCell/WalletModeCell";
import UserNote from "components/Ui/UserNote/UserNote";
import AppPresenceBadge from "components/Ui/AppPresenceBadge/AppPresenceBadge";
import { useSelector } from "react-redux";
import { selectAuth } from "store/authSlice";
import { ROLE_LABELS } from "constants/roleLabels";
import { useGetDriverPerformance } from "hooks/useUser";
import { formatMoney } from "utils/money";

const formatMinutes = (value, includeSign = false) => {
    if (value === null || value === undefined) return "—";
    const minutes = Number(value);
    const sign = includeSign && minutes > 0 ? "+" : "";
    return `${sign}${minutes.toFixed(1)} د`;
};

const formatRate = (value) => {
    if (value === null || value === undefined) return "—";
    return `${Math.round(Number(value) * 100)}%`;
};

/** «طلب واحد», «طلبان», «5 طلبات», «78 طلبًا». */
const formatOrderCount = (count) => {
    const n = Number(count) || 0;
    if (n === 1) return "طلب واحد";
    if (n === 2) return "طلبان";
    if (n >= 3 && n <= 10) return `${n} طلبات`;
    return `${n} طلبًا`;
};

/**
 * The average pickup against the restaurant's deadline, in words. Positive
 * means the driver was early or on time, the same sign as the API.
 */
const formatPickupDelta = (value) => {
    if (value === null || value === undefined) return null;
    const minutes = Number(value);
    if (Math.abs(minutes) < 0.05) return "بالمتوسط في الموعد تمامًا";
    const amount = `${Math.abs(minutes).toFixed(1)} د`;
    return minutes > 0 ? `بالمتوسط قبل الموعد بـ ${amount}` : `بالمتوسط بعد الموعد بـ ${amount}`;
};

/**
 * What the cell measures, for the tooltip, one line per point. «قبول: 1.6 د
 * (5)» said nothing on its own (the owner, 2026-09-27), so each line of the
 * cell names its figure, and this says how it is counted.
 */
const PERFORMANCE_EXPLAINED = [
    "القبول: متوسط الوقت من إنشاء الطلب حتى ضغط السائق زر «قبول».",
    "الاستلام: نسبة الطلبات التي استلمها السائق من المطعم قبل انتهاء وقت التحضير، ومتوسط الدقائق قبل الموعد أو بعده.",
    "لا يُحتسب في الاستلام إلا الطلب المكتمل الذي سُجّل استلامه.",
    "الفترة: آخر 30 يومًا. الرقم بين القوسين هو عدد الطلبات المحتسبة.",
];

const User = (props) => {
    const { userData } = useSelector(selectAuth);
    const canEditNotes = !!(userData?.super_admin || userData?.roles?.manage_users);
    const driverUserId = props.user?.role === "delivery" ? props.user.id : null;
    const {
        performance: driverPerformance,
        loading: performanceLoading,
        error: performanceError,
    } = useGetDriverPerformance(driverUserId);

    const cellRenderers = {
        select: () => (
            <td>
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={props.selectedUsers.includes(props.user.id)}
                    onChange={(e) => {
                        if (e.target.checked) {
                            props.setSelectedUsers((prev) => [...prev, props.user.id]);
                        } else {
                            props.setSelectedUsers((prev) =>
                                prev.filter((id) => id !== props.user.id),
                            );
                        }
                    }}
                />
            </td>
        ),
        id: () => <td>{props.user?.id}</td>,
        name: () => (
            <td className="name">
                <span>
                    <div>
                        <img
                            src={
                                props.user.image && props.user.image !== "null"
                                    ? props.user.image
                                    : notfound
                            }
                            alt="user"
                        />
                    </div>
                    <div>
                        <h6 style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span>{props.user?.name}</span>
                            <UserNote
                                userId={props.user?.id}
                                note={props.user?.note}
                                userName={props.user?.name}
                                editable={canEditNotes}
                                showMessage={props.showMessage}
                            />
                        </h6>
                        <p>{props.user?.phone}</p>
                    </div>
                </span>
            </td>
        ),
        address: () => <td>{props.user?.address || "لا يوجد"}</td>,
        city: () => <td>{props.user?.city ? props.user.city.name : "لا يوجد"}</td>,
        areasCount: () => (
            <td>{props.user?.delivery?.areas ? props.user.delivery.areas.length : "لا يوجد"}</td>
        ),
        // Which kind of admin, on the admins page. A super admin is not scoped
        // to a city and is the only role that can raise min_version, so the
        // distinction is the most consequential thing on an admin row.
        //
        // ONE SOURCE: `admins.super_admin`. It used to fall back to
        // `users.role`, which disagrees with the flag on real data — one
        // account carries role "super_admin" with the flag OFF. The نوع المشرف
        // FILTER reads the flag, so that row was listed under مشرف and labelled
        // مشرف عام at the same time. The flag is what every guard checks, so it
        // is the honest answer and the label follows it.
        //
        // `users.role` still names a NON-admin row — this renderer is shared
        // with the other people pages — but it can no longer contradict the
        // flag for an admin.
        maxConcurrentOrders: () =>
            props.user?.role === "delivery" && props.onSaveOrderLimit ? (
                <OrderLimitCell
                    value={props.user.delivery?.max_concurrent_orders}
                    onSave={(value) => props.onSaveOrderLimit(props.user, value)}
                />
            ) : (
                <td>—</td>
            ),
        // Whether this driver pays per order at all (api#137). Rendered only for
        // a caller that can save it, the same rule as the ceiling beside it — a
        // control that cannot write is worse than no control.
        walletMode: () =>
            props.user?.role === "delivery" && props.onSaveWalletMode ? (
                <WalletModeCell
                    enabled={props.user.delivery?.wallet_enabled}
                    onSave={(enabled) => props.onSaveWalletMode(props.user, enabled)}
                />
            ) : (
                <td>—</td>
            ),
        role: () => (
            <td>
                {props.user?.admin
                    ? props.user.admin.super_admin
                        ? ROLE_LABELS.super_admin
                        : ROLE_LABELS.admin
                    : ROLE_LABELS[props.user?.role] || props.user?.role}
            </td>
        ),
        appStatus: () => (
            <td>
                {props.user?.role === "delivery" ? (
                    <AppPresenceBadge
                        presence={props.user?.appPresence}
                        channel="driverApp"
                        neverLabel="لم يفتح التطبيق"
                        isOnline={!!props.user?.online}
                    />
                ) : (
                    // Vendors have their own التطبيق column on the vendors
                    // screen, where it sits beside their open/closed switch.
                    // Everyone else has no app to be open.
                    <span className="text-muted">—</span>
                )}
            </td>
        ),
        // Shared with the restaurants page — see BlockStatusButton for why the
        // archived state is separate from the blocked one.
        block: () => (
            <td className="cell--block">
                <BlockStatusButton account={props.user} onToggle={props.onToggleBlock} />
            </td>
        ),
        // The rate agreed with this driver, or his city's default shown as such.
        // See PercentageCell for why the two must look different.
        commissionPercentage: () =>
            props.onSavePercentage ? (
                <PercentageCell
                    ownValue={props.user?.delivery?.shipping_percentage}
                    effectiveValue={props.user?.effective_shipping_percentage}
                    onSave={(value) => props.onSavePercentage(props.user, value)}
                />
            ) : (
                <td>
                    <span className="text-muted">—</span>
                </td>
            ),
        // The cached `wallet_balance`, which the API writes in the same
        // transaction as the ledger row. Zero is a real balance and the one that
        // matters most — it is why a driver cannot take an order.
        //
        // A BUTTON, the same cell as the restaurants table (admin#41): it opens
        // the wallet, which is where the operator tops up. The chip says why a
        // driver on the wallet at ₪0 or less sees no orders.
        walletBalance: () => {
            if (props.user?.role !== "delivery") {
                return (
                    <td>
                        <span className="text-muted">—</span>
                    </td>
                );
            }

            const balance = Number(props.user?.delivery?.wallet_balance ?? 0);
            const onWallet = props.user?.delivery?.wallet_enabled === true;

            return (
                <td className="wallet-cell">
                    <button
                        type="button"
                        className={`wallet-cell__value${balance < 0 ? " wallet-cell__value--debt" : ""}`}
                        onClick={() => props.onOpenWallet?.(props.user)}
                        disabled={!props.onOpenWallet}
                        title="عرض المحفظة"
                    >
                        {formatMoney(balance)}
                    </button>
                    {onWallet && balance <= 0 ? (
                        <span className="wallet-cell__chip">بلا رصيد</span>
                    ) : null}
                </td>
            );
        },
        // Lifetime completed orders. Two fields, not one, because an account can
        // be both a driver and a customer — see util/orderParticipantCounts.js
        // on the API. Each page shows the one its role is about.
        completedOrdersMade: () => (
            <td>{Number(props.user?.completedOrdersMade || 0).toLocaleString("en-US")}</td>
        ),
        completedOrdersDelivered: () => (
            <td>{Number(props.user?.completedOrdersDelivered || 0).toLocaleString("en-US")}</td>
        ),
        // Driver rating — only meaningful for delivery users (the Delivery
        // model carries rating_avg/rating_count). Non-delivery rows show —.
        rating: () => (
            <td>
                {props.user?.delivery && props.user.delivery.rating_count > 0 ? (
                    <span style={{ color: "#f5a623", fontWeight: 600, whiteSpace: "nowrap" }}>
                        ★ {Number(props.user.delivery.rating_avg).toFixed(2)}{" "}
                        <span style={{ color: "#777", fontWeight: 400 }}>
                            ({props.user.delivery.rating_count})
                        </span>
                    </span>
                ) : (
                    <span style={{ color: "#aaa" }}>—</span>
                )}
            </td>
        ),
        performance: () => {
            if (props.user?.role !== "delivery") {
                return (
                    <td>
                        <span className="text-muted">—</span>
                    </td>
                );
            }

            if (performanceLoading) {
                return (
                    <td>
                        <span className="text-muted">جاري التحميل</span>
                    </td>
                );
            }

            if (performanceError || !driverPerformance) {
                return (
                    <td>
                        <span className="text-muted">بيانات غير متاحة</span>
                    </td>
                );
            }

            const pickup = driverPerformance.pickup;
            const acceptance = driverPerformance.acceptance;

            // WHY IT IS EMPTY, and it is not always the same reason (api#132).
            //
            // `picked_up_at` is written only by the picked-up status, which the
            // server refuses while its flag is off — so NO order can be scored,
            // for anybody. That used to render as «بيانات غير كافية», the same
            // words a brand-new driver gets, which made it impossible to tell a
            // switched-off feature from a driver with no history. The person who
            // has to sign the feature off could not confirm it worked either.
            if (driverPerformance.pickupTrackingEnabled === false) {
                return (
                    <td>
                        <HintTooltip lines={["تسجيل الاستلام من المطعم غير مفعّل على الخادم بعد."]}>
                            <span className="text-muted">تسجيل الاستلام غير مفعّل</span>
                        </HintTooltip>
                    </td>
                );
            }

            const hasAnyData = (acceptance?.sampleSize || 0) > 0 || (pickup?.sampleSize || 0) > 0;

            if (!hasAnyData) {
                return (
                    <td>
                        <HintTooltip lines={PERFORMANCE_EXPLAINED}>
                            <span className="text-muted">
                                لا يوجد قبول أو استلام في آخر 30 يومًا
                            </span>
                        </HintTooltip>
                    </td>
                );
            }

            // A SAMPLE SIZE IS THE RIGHT GUARD FOR RANKING DRIVERS AND THE WRONG
            // ONE FOR SEEING THE DATA. Below the threshold the figures are shown
            // in grey with the count beside them, rather than hidden: the first
            // nine scored orders per driver used to display nothing at all, so
            // nobody could confirm the feature worked without manufacturing ten
            // complete orders each. Keep the threshold for any sorting.
            const belowThreshold = !(pickup?.enoughData && acceptance?.enoughData);

            const pickupDelta = formatPickupDelta(pickup?.averageDeltaMinutes);

            return (
                <td className={belowThreshold ? "text-muted" : undefined}>
                    <HintTooltip
                        lines={
                            belowThreshold
                                ? [
                                      ...PERFORMANCE_EXPLAINED,
                                      `عيّنة صغيرة: المقارنة بين السائقين تحتاج ${driverPerformance.minimumSampleSize} طلبات على الأقل.`,
                                  ]
                                : PERFORMANCE_EXPLAINED
                        }
                    >
                        <div>
                            <div style={{ whiteSpace: "nowrap", lineHeight: 1.6 }}>
                                {acceptance?.sampleSize > 0 ? (
                                    <>
                                        القبول: خلال {formatMinutes(acceptance.averageMinutes)}{" "}
                                        <span className="text-muted">
                                            ({formatOrderCount(acceptance.sampleSize)})
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-muted">القبول: لا يوجد بعد</span>
                                )}
                            </div>
                            <div style={{ whiteSpace: "nowrap", lineHeight: 1.6 }}>
                                {pickup?.sampleSize > 0 ? (
                                    <>
                                        الاستلام: {formatRate(pickup.onTimeRate)} في الوقت{" "}
                                        <span className="text-muted">
                                            ({formatOrderCount(pickup.sampleSize)})
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-muted">الاستلام: لم يُسجَّل بعد</span>
                                )}
                            </div>
                            {pickup?.sampleSize > 0 && pickupDelta ? (
                                <div
                                    className="text-muted"
                                    style={{ whiteSpace: "nowrap", lineHeight: 1.6, fontSize: 12 }}
                                >
                                    {pickupDelta}
                                </div>
                            ) : null}
                        </div>
                    </HintTooltip>
                </td>
            );
        },
        actions: () => (
            <td>
                <DropDownButtons
                    user={props.user}
                    edit={() => {
                        props.setSelectedUserValue(props.user);
                        props.toggleEditForm();
                    }}
                    // Super admin only, same rule the endpoint enforces — a
                    // city admin offered the item would only ever get a 403.
                    // Null hides it, matching how impersonate is gated below.
                    delete={
                        userData?.super_admin
                            ? () => {
                                  props.setSelectedUserValue(props.user);
                                  props.toggleDelete();
                              }
                            : null
                    }
                    changePassword={() => {
                        props.setSelectedUserValue(props.user);
                        props.toggleChangePassword();
                    }}
                    // Drivers only. The page passes null for every other role,
                    // because only a driver has a wallet — see DriverWallet.
                    wallet={props.onOpenWallet ? () => props.onOpenWallet(props.user) : null}
                    // Restored: a rebase dropped this item, and the same rebase
                    // dropped the rate cell. Users.js passed the handler the
                    // whole time, so nothing failed — the item just went away.
                    impersonate={
                        userData?.super_admin && props.user.id !== userData?.id
                            ? () => props.onImpersonate(props.user)
                            : null
                    }
                ></DropDownButtons>
            </td>
        ),
    };

    return (
        // An archived account is dimmed rather than hidden: it is listed only
        // so an operator can restore it. See table.scss for why the dimming is
        // colour and not opacity.
        <tr className={`item${props.user?.archivedAt ? " item--archived" : ""}`}>
            <TableBodyCells visibleColumns={props.visibleColumns} renderers={cellRenderers} />
        </tr>
    );
};

export default User;
